import { DataTable, Dialog, FormInput, FormTextarea, Loader } from "@/components";
import { Button } from "@/components/ui";
import { Hash } from "lucide-react";
import { FormProvider } from "react-hook-form";
import { NumberRangesSectionProps } from "./types";
import useNumberRangesSection from "./useNumberRangesSection";
import { END_NUMBER, RANGE_DESCRIPTION, START_NUMBER, WARNING_PERCENTAGE } from "./consts";

const NumberRangesSection = ({
  ranges,
  onAddRange,
  isAddPending,
  allowAdd = true,
}: NumberRangesSectionProps) => {
  const { form, columns, onOpenAdd, onSave, open, setOpen } = useNumberRangesSection({onAddRange});
  const { errors } = form.formState;

  return (
    <>
      <div className="bg-white mt-5 rounded-lg px-[30px] py-[25px]">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-6">
          <div>
            <h4 className="flex items-center gap-2 text-[16px] font-semibold text-g-700">
              <Hash className="h-5 w-5" />
              Number Ranges
            </h4>
            <p className="text-[12px] text-gray-400 mt-1">
              Community specific. Numbers are issued in order from the active range (Start and End inclusive). Used ranges are kept as history.
            </p>
          </div>
          {allowAdd ? (
            <Button type="button" className="shrink-0" onClick={onOpenAdd} disabled={isAddPending}>
              {isAddPending ? <Loader variant="secondary" /> : <></>} + Add Number Range
            </Button>
          ) : null}
        </div>

        <DataTable
          columns={columns}
          data={ranges || []}
          hideFooter
          className="shadow-none rounded-none border-0"
          tableWrapperClassName="px-0"
        />
      </div>

      {open && (
        <Dialog
          open={open}
          setOpen={setOpen}
          title={
            <div className="text-2xl font-bold text-primary">
              Add Number Range
              <div className="h-1 w-[30%] bg-primary rounded-full" />
            </div>
          }
          saveText="Save"
          onSave={onSave}
          isLoading={isAddPending}
          contentClassName="md:w-[560px]"
        >
          <FormProvider {...form}>
            <div className="grid grid-cols-1 gap-5">
              <FormInput
                name={START_NUMBER}
                control={form.control}
                label="Start Number"
                placeholder="Please enter"
                error={errors[START_NUMBER]?.message}
                isRequired
                variant="integer"
              />
              <FormInput
                name={END_NUMBER}
                control={form.control}
                label="End Number"
                placeholder="Please enter"
                error={errors[END_NUMBER]?.message}
                isRequired
                variant="integer"
              />
              <FormInput
                name={WARNING_PERCENTAGE}
                control={form.control}
                label="Warning Threshold Percentage"
                placeholder="Please enter"
                error={errors[WARNING_PERCENTAGE]?.message}
                isRequired
                variant="integer"
              />
              <FormTextarea
                name={RANGE_DESCRIPTION}
                control={form.control}
                label="Description"
                placeholder="Please enter"
                error={errors[RANGE_DESCRIPTION]?.message}
              />
            </div>
          </FormProvider>
        </Dialog>
      )}
    </>
  );
};

export default NumberRangesSection;
